import type { PeriodType } from "@/apis/ixd-editor/types";
import type { TaggedPeriod } from "@/apis/ixd-value-sync/types";

/** 워크북 taggedPeriods 가 말해 주는 기간만 비교한다 — 전전기는 워크북에 없다. */
const COMPARED_PERIODS: readonly TaggedPeriod[] = ["CURRENT", "PREVIOUS"];

/** 같은 표인데 DSD 워크북과 IXD에 있는 기간(당기·전기)이 다른 것. 워크북이 기준이다. */
export interface TaggedPeriodMismatch {
  /** 워크북 표에 태깅된 기간 — IXD가 맞출 목표 */
  workbookPeriods: TaggedPeriod[];
  /** IXD 표에 있는 기간(당기·전기만) */
  ixdPeriods: TaggedPeriod[];
  /** IXD 표에는 있는데 워크북 표에는 태깅이 없는 기간 — 그 워크북 칸은 빈 표다 */
  missingInWorkbook: TaggedPeriod[];
  /** 워크북 표에는 태깅돼 있는데 IXD 표에 없는 기간 — 그 값은 반영할 자리가 없다 */
  missingInIxd: TaggedPeriod[];
}

/**
 * DSD 워크북과 IXD 표의 기간 구성이 다르면 그 차이, 아니면 null.
 *
 * - 워크북 표가 없으면(undefined — 아직 불러오지 않았거나 매칭된 표가 없음): 비교할 대상이
 *   없으니 null.
 * - 워크북 taggedPeriods 가 [] 면: 기간이 정해진 셀이 없어 모르는 것이지 "둘 다 없음"이
 *   아니라 null.
 * - IXD 기간은 표가 실제로 있는 기간(periodType)으로 본다. 매칭은 기간을 보지 않아 기간이
 *   엇갈려도 matched 로 온다.
 */
export const getTaggedPeriodMismatch = (
  workbookTaggedPeriods: readonly TaggedPeriod[] | undefined,
  ixdPeriodTypes: readonly PeriodType[],
): TaggedPeriodMismatch | null => {
  if (!workbookTaggedPeriods?.length || ixdPeriodTypes.length === 0) {
    return null;
  }
  const missingInWorkbook = COMPARED_PERIODS.filter(
    (period) =>
      ixdPeriodTypes.includes(period) &&
      !workbookTaggedPeriods.includes(period),
  );
  const missingInIxd = COMPARED_PERIODS.filter(
    (period) =>
      workbookTaggedPeriods.includes(period) &&
      !ixdPeriodTypes.includes(period),
  );
  if (missingInWorkbook.length === 0 && missingInIxd.length === 0) return null;
  return {
    workbookPeriods: COMPARED_PERIODS.filter((period) =>
      workbookTaggedPeriods.includes(period),
    ),
    ixdPeriods: COMPARED_PERIODS.filter((period) =>
      ixdPeriodTypes.includes(period),
    ),
    missingInWorkbook,
    missingInIxd,
  };
};

/**
 * IXD 편집기에서 워크북 기간에 맞추는 방법. 편집기 표 위 '당/전기 사용안함' 체크박스가
 * 한 기간만 쓰게 하고, 체크할 때 뜨는 확인 창에서 '예'면 전기만, '아니오'면 당기만 남는다.
 * 문구는 편집기 이름 그대로라야 사용자가 바로 찾는다 — 편집기 문구가 바뀌면 여기만 고친다.
 */
export const getTaggedPeriodMismatchAction = (
  mismatch: TaggedPeriodMismatch,
): string => {
  if (mismatch.workbookPeriods.length > 1) {
    return "IXD 편집기에서 '당/전기 사용안함' 체크를 해제해주세요.";
  }
  const answer = mismatch.workbookPeriods[0] === "PREVIOUS" ? "예" : "아니오";
  // IXD가 이미 한 기간만 쓰면 체크된 상태라, 다시 물어보게 하려면 해제했다가 체크해야 한다.
  const check =
    mismatch.ixdPeriods.length > 1
      ? "'당/전기 사용안함'을 체크하고"
      : "'당/전기 사용안함'을 해제했다가 다시 체크하고";
  return `IXD 편집기에서 ${check} 확인 창에서 '${answer}'를 선택해주세요.`;
};

/**
 * 기간 차이와 맞추는 방법을 알리는 문장. 기간 이름은 워크북 칸에 보이는 라벨(labelOf)을 써서
 * 화면에서 바로 짚을 수 있게 한다.
 */
export const getTaggedPeriodMismatchMessage = (
  mismatch: TaggedPeriodMismatch | null,
  labelOf: (period: TaggedPeriod) => string,
): string | null => {
  if (!mismatch) return null;
  const join = (periods: TaggedPeriod[]) => periods.map(labelOf).join("·");
  return [
    mismatch.missingInWorkbook.length > 0 &&
      `DSD 워크북 표에는 ${join(mismatch.missingInWorkbook)} 값이 없습니다.`,
    mismatch.missingInIxd.length > 0 &&
      `IXD 표에 ${join(mismatch.missingInIxd)} 표가 없어 DSD 워크북의 ${join(mismatch.missingInIxd)} 값은 반영되지 않습니다.`,
    getTaggedPeriodMismatchAction(mismatch),
  ]
    .filter(Boolean)
    .join(" ");
};
